import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import * as cognitoService from '../lib/auth/cognito.service';
import { useAuthStore } from '../stores/auth.store';
/**
 * Session inactivity timeout in milliseconds.
 * Users will be logged out after 30 minutes of no activity.
 */
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
/**
 * Warning shown before logout (optional, can show a modal in future)
 */
const WARNING_BEFORE_LOGOUT_MS = 2 * 60 * 1000; // 2 minutes before logout
/**
 * Events that reset the inactivity timer.
 * These indicate user activity in the browser.
 */
const ACTIVITY_EVENTS = [
    'mousedown',
    'mousemove',
    'keydown',
    'scroll',
    'touchstart',
    'click',
    'focus',
];
/**
 * Hook to track user inactivity and automatically log out after timeout.
 *
 * This is for portal users only - kiosk devices use long-lived tokens
 * and are not affected by this timeout.
 *
 * Security: After 30 minutes of no user activity (mouse, keyboard, touch),
 * the user will be logged out and redirected to the login page.
 */
export function useInactivityTimeout() {
    const navigate = useNavigate();
    const { logout: logoutStore, isAuthenticated } = useAuthStore();
    const timeoutRef = useRef(null);
    const warningTimeoutRef = useRef(null);
    const warningShownRef = useRef(false);
    /**
     * Perform logout due to inactivity
     */
    const handleInactivityLogout = useCallback(async () => {
        try {
            // Sign out from Cognito
            await cognitoService.logout();
        }
        catch (err) {
            console.error('Error during inactivity logout:', err);
        }
        // Clear local auth state
        logoutStore();
        // Show notification
        toast.warning('Session expired', {
            description: 'You have been logged out due to inactivity. Please sign in again.',
            duration: 5000,
        });
        // Redirect to login
        void navigate('/login', { replace: true });
    }, [logoutStore, navigate]);
    /**
     * Show warning before logout
     */
    const showWarning = useCallback(() => {
        if (!warningShownRef.current) {
            warningShownRef.current = true;
            toast.info('Session expiring soon', {
                description: 'Your session will expire in 2 minutes due to inactivity. Move your mouse or press a key to stay logged in.',
                duration: 10000,
            });
        }
    }, []);
    /**
     * Reset the inactivity timer on user activity
     */
    const resetTimer = useCallback(() => {
        // Clear existing timers
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (warningTimeoutRef.current) {
            clearTimeout(warningTimeoutRef.current);
        }
        // Reset warning flag
        warningShownRef.current = false;
        // Only set timers if user is authenticated
        if (!isAuthenticated) {
            return;
        }
        // Set warning timer (fires 2 minutes before logout)
        warningTimeoutRef.current = setTimeout(() => {
            showWarning();
        }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_LOGOUT_MS);
        // Set logout timer
        timeoutRef.current = setTimeout(() => {
            void handleInactivityLogout();
        }, INACTIVITY_TIMEOUT_MS);
    }, [isAuthenticated, handleInactivityLogout, showWarning]);
    useEffect(() => {
        // Don't track inactivity if not authenticated
        if (!isAuthenticated) {
            return;
        }
        // Add event listeners for all activity events
        const handleActivity = () => {
            resetTimer();
        };
        // Throttle the reset to avoid too many calls
        let lastReset = Date.now();
        const throttledHandleActivity = () => {
            const now = Date.now();
            // Only reset if at least 1 second has passed since last reset
            if (now - lastReset >= 1000) {
                lastReset = now;
                handleActivity();
            }
        };
        // Register event listeners
        ACTIVITY_EVENTS.forEach((event) => {
            window.addEventListener(event, throttledHandleActivity, { passive: true });
        });
        // Also listen for visibility change (user returns to tab)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // User returned to tab, reset timer
                handleActivity();
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        // Start the initial timer
        resetTimer();
        // Cleanup
        return () => {
            ACTIVITY_EVENTS.forEach((event) => {
                window.removeEventListener(event, throttledHandleActivity);
            });
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (warningTimeoutRef.current) {
                clearTimeout(warningTimeoutRef.current);
            }
        };
    }, [isAuthenticated, resetTimer]);
}
